/* eslint-disable react-hooks/set-state-in-effect */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { ChevronDown, ArrowUp, Paperclip, Check, CheckCheck, RotateCw } from "lucide-react";
import { useToast } from "@chakra-ui/react";
import { useSnapshot } from "valtio";
import { editorState } from "../../utils/editor";
import { shareFeedbackSettings } from "../../apis/settings/share.feedback.settings";
import { getMyConversation } from "../../apis/feedback/get.my.conversation";
import { markMyConversationRead } from "../../apis/feedback/mark.my.conversation.read";
import {
  Conversation,
  ConversationMessage,
  ConversationTicket,
  MessageReplyTo,
} from "../../types/feedback";
import { supportStore } from "../../utils/support";
import TicketCard from "./ticket-card";
import TicketThread from "./ticket-thread";
import AttachmentGallery from "./attachment-gallery";
import {
  useAttachmentUpload,
  HiddenFileInput,
  PendingPreviews,
} from "./attachment-upload";
import { QuotedBlock, ReplyingToBar, ReplyButton, previewOf } from "./reply";

const issueTypes = ["Issue", "Suggestion", "Shout out"];
const toApiOption = (label: string) => label.toLowerCase().replace(/\s+/g, "");

type Outgoing = {
  id: string;
  body: string;
  attachments: import("../../types/ticket").TicketAttachment[];
  replyTo?: MessageReplyTo;
  status: "sending" | "failed";
  createdAt: string;
};

type TimelineItem =
  | { kind: "message"; at: number; msg: ConversationMessage }
  | { kind: "ticket"; at: number; ticket: ConversationTicket };

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

const SupportChat: React.FC = () => {
  const { XAccountHash } = useSnapshot(editorState);
  const { liveTick } = useSnapshot(supportStore);

  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [selectedIssue, setSelectedIssue] = useState("Issue");
  const [showDropdown, setShowDropdown] = useState(false);
  const [outgoing, setOutgoing] = useState<Outgoing[]>([]);
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);
  const [replyTarget, setReplyTarget] = useState<MessageReplyTo | null>(null);
  const { pending, uploading, fileRef, pick, onFilesSelected, removeAt, reset } =
    useAttachmentUpload();
  const bottomRef = useRef<HTMLDivElement>(null);
  const firstTick = useRef(true);

  const toast = useToast({ position: "top", containerStyle: { zIndex: 9 } });

  const load = useCallback((markRead: boolean, onDone?: () => void) => {
    getMyConversation(
      {
        onCompleted({ data }) {
          setConversation(data);
          setLoading(false);
          onDone?.();
          if (markRead) {
            markMyConversationRead({ onCompleted() {}, onError() {} });
            supportStore.unreadCount = 0;
          }
        },
        onError() {
          setLoading(false);
        },
      },
      { bypassCache: true }
    );
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  useEffect(() => {
    if (firstTick.current) {
      firstTick.current = false;
      return;
    }
    load(true);
  }, [liveTick, load]);

  const timeline: TimelineItem[] = useMemo(() => {
    if (!conversation) return [];
    const items: TimelineItem[] = [];
    for (const msg of conversation.messages ?? []) {
      items.push({ kind: "message", at: new Date(msg.createdAt).getTime(), msg });
    }
    for (const ticket of conversation.tickets ?? []) {
      items.push({
        kind: "ticket",
        at: new Date(ticket.createdAt).getTime(),
        ticket,
      });
    }
    items.sort((a, b) => a.at - b.at);
    return items;
  }, [conversation]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [timeline.length, outgoing.length]);

  // --- Sending ---
  const dispatch = (item: Outgoing) => {
    setOutgoing((prev) =>
      prev.map((o) => (o.id === item.id ? { ...o, status: "sending" } : o))
    );
    shareFeedbackSettings({
      headers: { "X-Account-Hash": XAccountHash },
      body: {
        feedbackOptions: toApiOption(selectedIssue),
        message: item.body,
        attachments: item.attachments.length ? item.attachments : undefined,
        replyTo: item.replyTo,
      },
      onCompleted() {
        load(false, () =>
          setOutgoing((prev) => prev.filter((o) => o.id !== item.id))
        );
      },
      onError() {
        setOutgoing((prev) =>
          prev.map((o) => (o.id === item.id ? { ...o, status: "failed" } : o))
        );
      },
    });
  };

  const handleSend = () => {
    const text = message.trim();
    if (!text && pending.length === 0) return;
    const item: Outgoing = {
      id: `${Date.now()}-${Math.round(Math.random() * 1e6)}`,
      body: text,
      attachments: pending,
      replyTo: replyTarget ?? undefined,
      status: "sending",
      createdAt: new Date().toISOString(),
    };
    setOutgoing((prev) => [...prev, item]);
    setMessage("");
    reset();
    setReplyTarget(null);
    dispatch(item);
  };

  const startReply = (m: { _id: string; sender: "user" | "admin"; body?: string; attachments?: import("../../types/ticket").TicketAttachment[] }) =>
    setReplyTarget({
      messageID: m._id,
      sender: m.sender,
      preview: previewOf(m),
    });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // A ticket is open: show it as its own thread (single composer), not inline.
  if (openTicketId) {
    return (
      <TicketThread
        ticketId={openTicketId}
        onBack={() => {
          setOpenTicketId(null);
          load(false);
        }}
      />
    );
  }

  return (
    <div className="flex flex-col h-full w-full min-h-0">
      <div className="flex-1 px-6 py-6 overflow-y-auto min-h-0">
        {loading && (
          <p className="text-center text-gray-400 text-sm">Loading…</p>
        )}

        {!loading && timeline.length === 0 && outgoing.length === 0 && (
          <p className="text-center text-gray-400 text-sm mt-6">
            Send us a message and we'll get back to you.
          </p>
        )}

        {timeline.map((item) => {
          if (item.kind === "ticket") {
            return (
              <TicketCard
                key={`t-${item.ticket._id}`}
                ticket={item.ticket}
                onOpen={setOpenTicketId}
              />
            );
          }
          const msg = item.msg;
          const isUser = msg.sender === "user";
          return (
            <div
              key={msg._id}
              className={`mb-6 flex items-center gap-1 ${
                isUser ? "justify-end" : "justify-start"
              }`}
            >
              {isUser && <ReplyButton onClick={() => startReply(msg)} />}
              <div
                className={`max-w-[85%] rounded-lg px-4 py-3 shadow-sm ${
                  isUser ? "bg-white" : "bg-[#FFE4E4]"
                }`}
              >
                {msg.replyTo && (
                  <QuotedBlock replyTo={msg.replyTo} viewerSide="user" />
                )}
                {msg.body && (
                  <p className="text-gray-900 text-sm leading-relaxed whitespace-pre-wrap break-words">
                    {msg.body}
                  </p>
                )}
                <AttachmentGallery items={msg.attachments} />
                <div className="flex items-center justify-end gap-1 mt-1">
                  <span className="text-[10px] text-gray-400 whitespace-nowrap">
                    {formatTime(msg.createdAt)}
                  </span>
                  {isUser && (
                    <CheckCheck size={14} className="text-gray-400 shrink-0" />
                  )}
                </div>
              </div>
              {!isUser && <ReplyButton onClick={() => startReply(msg)} />}
            </div>
          );
        })}

        {outgoing.map((o) => {
          const failed = o.status === "failed";
          return (
            <div key={o.id} className="mb-6 flex justify-end">
              <div className="max-w-[85%] flex flex-col items-end">
                <div className="rounded-lg px-4 py-3 shadow-sm bg-white">
                  {o.replyTo && (
                    <QuotedBlock replyTo={o.replyTo} viewerSide="user" />
                  )}
                  {o.body && (
                    <p className="text-gray-900 text-sm leading-relaxed whitespace-pre-wrap break-words">
                      {o.body}
                    </p>
                  )}
                  <AttachmentGallery items={o.attachments} />
                  <div className="flex items-center justify-end gap-1 mt-1">
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">
                      {formatTime(o.createdAt)}
                    </span>
                    {!failed && (
                      <Check size={14} className="text-gray-400 shrink-0" />
                    )}
                  </div>
                </div>
                {failed && (
                  <button
                    onClick={() => dispatch(o)}
                    className="mt-1 flex items-center gap-1 text-[11px] text-red-500 hover:text-red-600"
                  >
                    <RotateCw size={12} /> Couldn't send · Retry
                  </button>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="px-6 pb-6 pt-4 bg-[#F9F9F9]">
        {replyTarget && (
          <ReplyingToBar
            target={replyTarget}
            viewerSide="user"
            onCancel={() => setReplyTarget(null)}
          />
        )}
        <PendingPreviews items={pending} onRemove={removeAt} />

        <div className="relative">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message"
            className="w-full min-h-[60px] max-h-[120px] p-4 pb-12 pr-28 rounded-lg border border-gray-200 bg-white resize-none focus:outline-none focus:border-gray-300 text-gray-900 placeholder:text-gray-400"
          />

          <div className="absolute bottom-3 left-3">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FFE4E4] text-gray-900 text-xs hover:bg-[#ffd4d4] transition-colors"
            >
              <span>{selectedIssue}</span>
              <ChevronDown size={14} />
            </button>

            {showDropdown && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowDropdown(false)}
                />
                <div className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                  {issueTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => {
                        setSelectedIssue(type);
                        setShowDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-900 hover:bg-gray-50 transition-colors"
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="absolute bottom-3 right-3 flex items-center gap-2">
            <HiddenFileInput fileRef={fileRef} onChange={onFilesSelected} />
            <button
              onClick={pick}
              disabled={uploading}
              className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors disabled:opacity-50"
              aria-label="Attach a screenshot or video"
            >
              {uploading ? (
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Paperclip size={16} className="text-gray-600" />
              )}
            </button>
            <button
              onClick={handleSend}
              disabled={!message.trim() && pending.length === 0}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                message.trim() || pending.length
                  ? "bg-[#1E3A8A] hover:bg-[#1e40af]"
                  : "bg-gray-300"
              }`}
            >
              <ArrowUp
                size={20}
                className={
                  message.trim() || pending.length
                    ? "text-white"
                    : "text-gray-500"
                }
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportChat;
